// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import notify from '../utils/toast';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';

export default function Chat() {
    const { user } = useAuth();
    const { t } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const bookingId = searchParams.get('booking_id') || '';
    const customerId = searchParams.get('customer_id') || '';
    const isStaff = user.role !== 'customer';

    const [threads, setThreads] = useState([]);
    const [activeThread, setActiveThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const bottomRef = useRef(null);
    const listRef = useRef(null);

    const loadThreads = () => {
        api.get('/chat/threads').then((res) => {
            const list = res.data.data || [];
            setThreads(list);
            // Auto-select from URL params
            let pre = null;
            if (customerId) pre = list.find((x) => String(x.customer?.id) === String(customerId) && String(x.booking_id || '') === String(bookingId));
            if (!pre && customerId) pre = list.find((x) => String(x.customer?.id) === String(customerId));
            if (!pre && bookingId) pre = list.find((x) => String(x.booking_id) === String(bookingId));
            if (!pre && list.length) pre = list[0];
            if (pre) setActiveThread(pre);
            else if (!isStaff && list.length) setActiveThread(list[0]);
        }).catch(() => {});
    };

    const loadMessages = (thread) => {
        if (!thread) return;
        const params = {};
        if (isStaff && thread.customer?.id && thread.customer.id !== user.id) params.customer_id = thread.customer.id;
        if (thread.booking_id) params.booking_id = thread.booking_id;
        api.get('/chat/messages', { params })
            .then((res) => setMessages(res.data.data || []))
            .finally(() => setLoading(false));
    };

    useEffect(() => { loadThreads(); }, [customerId, bookingId]);

    useEffect(() => {
        setLoading(true);
        setMessages([]);
        loadMessages(activeThread);
        if (activeThread) {
            const p = {};
            if (activeThread.booking_id) p.booking_id = activeThread.booking_id;
            if (isStaff && activeThread.customer?.id && activeThread.customer.id !== user.id) p.customer_id = activeThread.customer.id;
            setSearchParams(p, { replace: true });
        }
    }, [activeThread?.key]);

    useEffect(() => {
        const id = setInterval(() => { loadMessages(activeThread); loadThreads(); }, 8000);
        return () => clearInterval(id);
    }, [activeThread]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const selectThread = (th) => setActiveThread(th);

    const send = async (e) => {
        e.preventDefault();
        if (!text.trim() || !activeThread) return;
        setSending(true);
        try {
            await api.post('/chat/messages', {
                message: text,
                booking_id: activeThread.booking_id || bookingId || null,
                receiver_id: isStaff ? (activeThread.customer?.id || null) : null,
                customer_id: isStaff ? (activeThread.customer?.id || null) : undefined,
            });
            setText('');
            loadMessages(activeThread);
            loadThreads();
        } catch (err) {
            notify.apiError(err);
        } finally {
            setSending(false);
        }
    };

    const initials = (name) => (name || '?').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
    const fmtTime = (d) => (d ? new Date(d).toLocaleString() : '');

    return (
        <div>
            <h4 className="mb-1">{t('supportChat') || 'Support Chat'}</h4>
            <p className="text-muted mb-3">{t('chatPageDesc') || 'Chat with customers and support team.'}</p>

            <div className="card table-card">
                <div className="row g-0" style={{ minHeight: 560 }}>
                    {/* Conversation list */}
                    <div className="col-12 col-md-4 col-lg-3 border-end d-flex flex-column" style={{ maxHeight: 640 }}>
                        <div className="p-2 border-bottom fw-semibold small">{t('conversations') || 'Conversations'}</div>
                        <div className="overflow-auto flex-grow-1" ref={listRef}>
                            {threads.length === 0 && (
                                <div className="text-muted small text-center py-4">No conversations yet</div>
                            )}
                            {threads.map((th) => (
                                <button
                                    key={th.key}
                                    type="button"
                                    onClick={() => selectThread(th)}
                                    className={`d-flex align-items-center gap-2 w-100 text-start px-3 py-2 border-0 chat-thread-item ${activeThread?.key === th.key ? 'active' : ''}`}
                                    style={{ background: activeThread?.key === th.key ? 'var(--primary-light, #2d8a45)' : 'transparent', color: activeThread?.key === th.key ? '#fff' : 'inherit' }}
                                >
                                    <span className="user-avatar flex-shrink-0" style={{ width: 36, height: 36, fontSize: 13 }}>
                                        {initials(th.customer?.name)}
                                    </span>
                                    <span className="flex-grow-1 text-truncate">
                                        <span className="d-block small fw-semibold text-truncate">
                                            {th.customer?.name || 'Unknown'}
                                            {th.booking_id && <span className="badge bg-secondary ms-1" style={{ fontSize: 9 }}>B#{th.booking_id}</span>}
                                        </span>
                                        <span className="d-block small text-truncate" style={{ opacity: 0.7 }}>
                                            {th.last_message || '—'}
                                        </span>
                                    </span>
                                    <span className="text-end flex-shrink-0">
                                        <span className="d-block small" style={{ fontSize: 10, opacity: 0.6 }}>
                                            {th.last_at ? new Date(th.last_at).toLocaleDateString() : ''}
                                        </span>
                                        {th.unread > 0 && (
                                            <span className="badge rounded-pill bg-danger" style={{ fontSize: 9 }}>{th.unread}</span>
                                        )}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Chat view */}
                    <div className="col-12 col-md-8 col-lg-9 d-flex flex-column" style={{ maxHeight: 640 }}>
                        {activeThread ? (
                            <>
                                <div className="px-3 py-2 border-bottom d-flex align-items-center gap-2">
                                    <span className="user-avatar" style={{ width: 32, height: 32, fontSize: 12 }}>
                                        {initials(activeThread.customer?.name)}
                                    </span>
                                    <div>
                                        <div className="fw-semibold small mb-0">{activeThread.customer?.name}</div>
                                        <div className="text-muted" style={{ fontSize: 11 }}>
                                            {activeThread.customer?.phone || activeThread.customer?.email || ''}
                                            {activeThread.booking_id ? ` · Booking #${activeThread.booking_id}` : ''}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex-grow-1 overflow-auto px-3 py-3" style={{ background: 'var(--bg-soft, rgba(0,0,0,0.02))' }}>
                                    {loading ? (
                                        <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-success" /></div>
                                    ) : messages.length === 0 ? (
                                        <div className="text-center text-muted py-5">{t('noMessagesYet') || 'No messages yet. Say hello!'}</div>
                                    ) : (
                                        messages.map((m) => {
                                            const mine = m.sender_id === user.id;
                                            return (
                                                <div key={m.id} className={`d-flex mb-2 ${mine ? 'justify-content-end' : 'justify-content-start'}`}>
                                                    <div
                                                        className={`rounded-3 px-3 py-2 shadow-sm ${mine ? 'text-white' : 'border'}`}
                                                        style={{
                                                            maxWidth: '75%',
                                                            background: mine ? 'var(--primary, #1a5f2a)' : undefined,
                                                            borderBottomRightRadius: mine ? 4 : undefined,
                                                            borderBottomLeftRadius: mine ? undefined : 4,
                                                        }}
                                                    >
                                                        {!mine && <div className="small fw-semibold mb-1 opacity-75">{m.sender?.name}</div>}
                                                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.message}</div>
                                                        <div className={`small mt-1 ${mine ? 'text-white-50' : 'text-muted'}`} style={{ fontSize: 10 }}>
                                                            {fmtTime(m.created_at)}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                    <div ref={bottomRef} />
                                </div>

                                <form onSubmit={send} className="d-flex gap-2 p-2 border-top">
                                    <input
                                        className="form-control"
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        placeholder={t('typeMessage') || 'Type a message…'}
                                    />
                                    <button type="submit" className="btn btn-primary" disabled={sending || !text.trim()}>
                                        <i className="bi bi-send"></i>
                                    </button>
                                </form>
                            </>
                        ) : (
                            <div className="d-flex align-items-center justify-content-center flex-grow-1 text-muted">
                                {t('selectConversation') || 'Select a conversation to start chatting'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}